// react
import { useCallback, useEffect, useMemo } from 'react';

// components
import useSearchParams from '@/hooks/useStableSearchParams';

const TIME_AGGREGATE_PARAM_KEY = 'timeAggregate';
export const TIME_AGGREGATE_VALUES = ['hour', 'day', 'week', 'month', 'year'];

export function useTimeAggregate({
  defaultValue = 'day',
  paramKey = TIME_AGGREGATE_PARAM_KEY,
  allowedValues = TIME_AGGREGATE_VALUES,
} = {}) {
  const [searchParams, setSearchParams] = useSearchParams();

  const raw = searchParams.get(paramKey);

  const aggregate = useMemo(
    () => (allowedValues.includes(raw) ? raw : defaultValue),
    [raw, allowedValues, defaultValue],
  );

  // Normalize URL on mount + whenever raw changes
  useEffect(() => {
    setSearchParams(
      (prev) => {
        const sp = new URLSearchParams(prev);
        const raw = sp.get(paramKey);
        const aggregate = allowedValues.includes(raw) ? raw : defaultValue;
        if (raw === aggregate) return prev;

        sp.set(paramKey, aggregate);
        return sp;
      },
      { replace: true },
    );
  }, [allowedValues, defaultValue, paramKey, setSearchParams]);

  const setAggregate = useCallback(
    (nextValue, { push = false } = {}) => {
      const safe = allowedValues.includes(nextValue) ? nextValue : defaultValue;

      setSearchParams(
        (prev) => {
          const sp = new URLSearchParams(prev);
          sp.set(paramKey, safe);
          return sp;
        },
        { replace: !push },
      );
    },
    [allowedValues, defaultValue, paramKey, setSearchParams],
  );

  return [aggregate, setAggregate];
}
